/**
 * Smart cache utility for dashboard data
 */
class SmartCache {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.TTL = 30000; // 30 seconds default TTL
  }

  /**
   * Get cached data if still valid
   */
  get(key) {
    const timestamp = this.timestamps.get(key);
    if (!timestamp) return null;

    const now = Date.now();
    const isValid = (now - timestamp) < this.TTL;

    if (!isValid) {
      this.delete(key);
      return null;
    }

    return this.cache.get(key);
  }

  /**
   * Set data in cache with timestamp
   */
  set(key, data) {
    this.cache.set(key, data);
    this.timestamps.set(key, Date.now());
  }

  /**
   * Check if data is stale but still in cache (for background refresh)
   */
  isStale(key) {
    const timestamp = this.timestamps.get(key);
    if (!timestamp) return false;

    const now = Date.now();
    const age = now - timestamp;
    
    // Data is stale if it's more than 20 seconds old but less than 30 seconds (TTL)
    return age > 20000 && age < this.TTL;
  }

  /**
   * Delete specific cache entry
   */
  delete(key) {
    this.cache.delete(key);
    this.timestamps.delete(key);
  }

  /**
   * Clear all cached data
   */
  clear() {
    this.cache.clear();
    this.timestamps.clear();
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const now = Date.now();
    const entries = Array.from(this.timestamps.entries()).map(([key, timestamp]) => ({
      key,
      age: now - timestamp,
      isValid: (now - timestamp) < this.TTL,
      isStale: this.isStale(key)
    }));

    return {
      totalEntries: this.cache.size,
      validEntries: entries.filter(e => e.isValid).length,
      staleEntries: entries.filter(e => e.isStale).length,
      entries
    };
  }

  /**
   * Invalidate cache entries that match a pattern
   * Useful when claims are created/updated
   */
  invalidatePattern(pattern) {
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.delete(key));
    return keysToDelete.length;
  }
}

export default SmartCache;
